<script setup lang="ts">
import { SancareToast } from '@sancare/typescript-commons'
import dayjs from '@sancare/typescript-commons/src/misc/dayjs.ts'
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useStore } from 'vuex'

import { hasPermission, RoleEnum } from '@/misc/permissions/permissionsHelper'
import router from '@/router'
import { RootState } from '@/store/types'

import LoginModal from './login/LoginModal.vue'
import StayFinder from './stay-displayer/StayFinder.vue'

const store = useStore<RootState>()
const route = useRoute()

const ieMsgClosed = ref(false)

const toast = computed(() => store.state.toast)
const currentUser = computed(() => store.state.login.currentUser)
const getSelfRequest = computed(() => store.state.login.getSelfRequest)
const isAuthenticated = computed(() => store.getters['isAuthenticated'])
const userRole = computed(() => store.getters['userRole'])
const username = computed(() => store.getters['username'])

const appVersion = computed(() => import.meta.env.VITE_APP_VERSION)
const appBuildDate = computed(() => dayjs(import.meta.env.VITE_APP_BUILD_DATE))
const sessionIsValid = computed(() => currentUser.value.id !== null)

function isIE() {
  return window.navigator && /Trident|MSIE/i.test(window.navigator.userAgent)
}

async function loadMedicalUnitFilterData() {
  store.dispatch('remoteLists/fetchConstants', {
    url: '/api/stay-search-criteria/list?type=constant',
  })
  store.dispatch('remoteLists/fetchDataPresence', {
    url: '/api/stay-search-criteria/presence',
  })
  await store.dispatch('remoteLists/fetchMedicalUnits', {
    url: '/api/stay-search-criteria/list?type=medicalUnit',
  })
  const { medicalUnits, facilities, divisions } = store.state.remoteLists
  store.commit('initMedicalUnits', { medicalUnits, facilities, divisions })
}

watch(sessionIsValid, (sess) => {
  if (sess) {
    store.dispatch('fetchSettings')
    store.dispatch('getUserSettings')
    if (hasPermission(RoleEnum.ROLE_STAY_READER, userRole.value)) {
      loadMedicalUnitFilterData()
    }
  }
})

watch(isAuthenticated, (isAuth, wasAuthenticated) => {
  if (isAuth && !wasAuthenticated) {
    store.dispatch('getSelf')
    // Default route when user is ROLE_DATA_PROVIDER is manage-api-key
    const redirectPath = route.query.redirect || (userRole.value === 'ROLE_DATA_PROVIDER' ? '/manage-api-key' : '/')
    router.push(redirectPath as string)
  } else if (!isAuth) {
    router.push({
      path: '/login',
      query: {
        redirect: route.fullPath,
      }
    })
  }
})

onMounted(async () => {
  await store.dispatch('getSelf')
  if (isAuthenticated.value && !sessionIsValid.value) {
    store.commit('sessionExpired')
  } else if (username.value && !isAuthenticated.value) {
    store.dispatch('logout')
  }
})
</script>

<template>
  <div
    class="full-height"
    :class="isAuthenticated ? '' : 'unlogged-page'"
  >
    <div
      v-if="isIE() && !ieMsgClosed"
      id="ie-msg"
      class="alert alert-danger"
    >
      Attention, Internet Explorer n'est plus supporté depuis Janvier 2022. Veuillez utiliser un autre navigateur (Mozilla Firefox, Google Chrome ou autre...) ou contacter votre support informatique.
      <button
        type="button"
        class="close"
        @click="ieMsgClosed = true"
      >
        <span aria-hidden="true">&times;</span>
      </button>
    </div>
    <login-modal v-if="!getSelfRequest.fetching && isAuthenticated && !sessionIsValid" />
    <stay-finder v-if="isAuthenticated" />
    <div class="full-height">
      <router-view class="full-height-leaf" />
      <sancare-toast
        :color="toast.color"
        :is-open="toast.isOpen"
        :message="toast.message"
        :title="toast.title"
        @close-toast="$store.commit('toast/setIsOpen', false)"
      />
      <footer class="text-center fst-italic">
        Sancare Report Viewer {{ appVersion }} ({{ appBuildDate.format('DD-MM-YYYY') }})
      </footer>
    </div>
  </div>
</template>

<style lang="scss">
  #ie-msg {
    position: fixed;
    bottom: 0;
    width: 100%;
    z-index: 9;
    text-align: center;
  }
</style>
